import React, { useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import PolandMapSVG from './PolandMapSVG';
import PartnerLogo from '@assets/svg-inline/partner-ipos-logo.svg';
import {
  Dealer,
  DealerCategory,
  DealersByVoivodeship,
  VoivodeshipId,
  DEALER_CATEGORY_LABELS,
  VOIVODESHIP_NAMES,
} from './voivodeships';

interface DealersMapProps {
  title?: string;
  lead?: string;
  sectionLabel?: string;
  hint?: string;
  emptyState?: string;
  tabAllLabel?: string;
  tabHorecaLabel?: string;
  dealersByVoivodeship: DealersByVoivodeship;
}

const PageHeader = styled.header`
  max-width: 1100px;
  margin: 48px auto 0;
  padding: 0 24px;
  text-align: center;

  @media (max-width: 860px) {
    margin-top: 32px;
  }
  @media (max-width: 480px) {
    margin-top: 24px;
  }
`;

const PageHeaderLogo = styled.div`
  display: inline-block;
  margin-bottom: 24px;

  svg {
    height: 72px;
    width: auto;
    display: block;
  }
`;

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
  margin-bottom: 20px;

  @media (max-width: 860px) {
    font-size: 28px;
  }
  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const PageLead = styled.p`
  font-size: 14px;
  color: #888;
  line-height: 1.7;
  max-width: 67%;
  margin: 0 auto;

  @media (max-width: 860px) {
    max-width: 85%;
  }
  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const TabsContainer = styled.div`
  max-width: 1100px;
  margin: 32px auto 0;
  padding: 0 24px;
  display: flex;
  justify-content: center;
  border-bottom: 2px solid #e2e2e2;

  @media (max-width: 600px) {
    flex-direction: column;
    border-bottom: none;
  }
`;

const TabButton = styled.button<{ isActive: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => (props.isActive ? '#1ca039' : 'transparent')};
  margin-bottom: -2px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: ${props => (props.isActive ? '#1ca039' : '#aaa')};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  line-height: 1.4;
  text-align: center;

  &:hover {
    color: #1ca039;
  }

  @media (max-width: 600px) {
    padding: 10px 0;
    border-bottom-width: 2px;
    margin-bottom: 0;

    & + & {
      border-top: 1px solid #e2e2e2;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  gap: 48px;
  align-items: flex-start;
  max-width: 1100px;
  margin: 48px auto;
  padding: 0 24px;

  @media (max-width: 860px) {
    flex-direction: column;
    gap: 32px;
    margin: 24px auto;
  }
  @media (max-width: 480px) {
    gap: 20px;
    margin: 16px auto;
    padding: 0 16px;
  }
`;

const MapColumn = styled.div`
  flex: 0 0 580px;

  @media (max-width: 860px) {
    flex: none;
    width: 100%;
  }
`;

const InfoColumn = styled.div`
  flex: 1;
  min-width: 200px;
  position: sticky;
  top: 48px;

  @media (max-width: 860px) {
    position: static;
    width: 100%;
  }
`;

const RegionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const RegionDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1ca039;
  flex-shrink: 0;
`;

const RegionName = styled.h2`
  font-size: 21px;
  font-weight: 700;
  color: #1ca039;
  line-height: 1.2;
  margin: 0;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 12px;
`;

const CompanyCard = styled.div`
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 13px 16px;
  margin-bottom: 8px;
  transition: border-color 0.15s;
  user-select: text;

  &:hover {
    border-color: #1ca039;
  }
`;

const CompanyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 3px;
`;

const CompanyDetail = styled.div`
  font-size: 12px;
  color: #888;
`;

const EmptyState = styled.p`
  font-size: 13px;
  color: #888;
  line-height: 1.6;
  padding: 12px 0;
`;

const Hint = styled.p`
  margin-top: 20px;
  font-size: 12px;
  color: #c0c0c0;
  line-height: 1.6;
  white-space: pre-line;
`;

const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(hover: none)').matches;
};

const DEFAULT_REGION: VoivodeshipId = 'mazowieckie';

const DealersMap: React.FC<DealersMapProps> = ({
  title,
  lead,
  sectionLabel,
  hint,
  emptyState,
  tabAllLabel,
  tabHorecaLabel,
  dealersByVoivodeship,
}) => {
  const [activeRegion, setActiveRegion] = useState<VoivodeshipId>(DEFAULT_REGION);
  const [lockedRegion, setLockedRegion] = useState<VoivodeshipId | null>(null);
  const [activeCategory, setActiveCategory] = useState<DealerCategory>('all');
  const infoPanelRef = useRef<HTMLDivElement>(null);

  const handleRegionEnter = useCallback(
    (id: VoivodeshipId) => {
      if (lockedRegion !== null) {
        return;
      }

      if (!isTouchDevice()) {
        setActiveRegion(id);
      }
    },
    [lockedRegion]
  );

  const handleRegionClick = useCallback(
    (id: VoivodeshipId) => {
      if (lockedRegion === id) {
        setLockedRegion(null);
      } else {
        setActiveRegion(id);
        setLockedRegion(id);
      }
      if (isTouchDevice() && infoPanelRef.current) {
        const fixedHeader = Array.from(document.querySelectorAll('header')).find(
          el => window.getComputedStyle(el).position === 'fixed'
        );
        const headerOffset = fixedHeader ? fixedHeader.getBoundingClientRect().height : 0;
        const targetTop =
          infoPanelRef.current.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      }
    },
    [lockedRegion]
  );

  const dealers: Dealer[] = (dealersByVoivodeship[activeRegion] || []).filter(dealer =>
    dealer.categories.includes(activeCategory)
  );
  const hasHeader = !!title || !!lead;

  const tabs: { id: DealerCategory; label: string }[] = [
    { id: 'all', label: tabAllLabel || DEALER_CATEGORY_LABELS.all },
    { id: 'horeca', label: tabHorecaLabel || DEALER_CATEGORY_LABELS.horeca },
  ];

  return (
    <>
      {hasHeader && (
        <PageHeader>
          <PageHeaderLogo>
            <PartnerLogo />
          </PageHeaderLogo>
          {title && <PageTitle>{title}</PageTitle>}
          {lead && <PageLead dangerouslySetInnerHTML={{ __html: lead }} />}
        </PageHeader>
      )}
      <TabsContainer role="tablist">
        {tabs.map(tab => (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeCategory === tab.id}
            isActive={activeCategory === tab.id}
            onClick={() => setActiveCategory(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabsContainer>
      <Wrapper>
        <MapColumn>
          <PolandMapSVG
            activeId={lockedRegion}
            onRegionEnter={handleRegionEnter}
            onRegionClick={handleRegionClick}
          />
        </MapColumn>
        <InfoColumn ref={infoPanelRef}>
          <RegionHeading>
            <RegionDot />
            <RegionName>{VOIVODESHIP_NAMES[activeRegion]}</RegionName>
          </RegionHeading>
          {sectionLabel && <SectionLabel>{sectionLabel}</SectionLabel>}
          {dealers.length > 0
            ? dealers.map(dealer => (
                <CompanyCard key={dealer.id}>
                  <CompanyName>{dealer.title}</CompanyName>
                  <CompanyDetail>{dealer.detail}</CompanyDetail>
                </CompanyCard>
              ))
            : emptyState && <EmptyState>{emptyState}</EmptyState>}
          {hint && <Hint>{hint}</Hint>}
        </InfoColumn>
      </Wrapper>
    </>
  );
};

export default DealersMap;
